﻿import * as React from "react";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";

// End material-ui type additions
const COLORS = {
    METRICELL_BLUE: `#00ABDA`,
    METRICELL_DARK_GREY: `#283845`,
    METRICELL_DARK_GREY2: `#151D24`,
    METRICELL_LIGHT_GREY: `#395364`,
    METRICELL_WHITE: `#FFFFFF`,
};

export const APP_THEME = createMuiTheme({
    overrides: {
        /* eslint-disable @typescript-eslint/naming-convention */
        MuiButton: {
            contained: {
                "&:hover": {
                    backgroundColor: COLORS.METRICELL_BLUE,
                },
                backgroundColor: COLORS.METRICELL_DARK_GREY2,
                boxShadow: `none`,
                color: COLORS.METRICELL_WHITE,
                textTransform: `none`,
            },
            containedPrimary: {
                "&:hover": {
                    backgroundColor: COLORS.METRICELL_BLUE,
                },
                backgroundColor: COLORS.METRICELL_DARK_GREY2,
                boxShadow: `none`,
                color: COLORS.METRICELL_WHITE,
                textTransform: `none`,
            },
            containedSecondary: {
                "&:hover": {
                    backgroundColor: COLORS.METRICELL_BLUE,
                },
                backgroundColor: `inherit`,
                boxShadow: `none`,
                textTransform: `none`,
            },
            textPrimary: {
                "&:hover": {
                    backgroundColor: COLORS.METRICELL_BLUE,
                },
                backgroundColor: COLORS.METRICELL_DARK_GREY2,
                boxShadow: `none`,
                color: COLORS.METRICELL_WHITE,
                textTransform: `none`,
            },
            textSecondary: {
                color: COLORS.METRICELL_WHITE,
                textTransform: `none`,
            },
        },
        MuiButtonBase: {
            root: {
                display: `flex`,
            },
        },
        MuiCheckbox: {
            colorSecondary: {
                "&$checked": {
                    "&:hover": {
                        backgroundColor: `transparent`,
                    },
                    color: COLORS.METRICELL_BLUE,
                },
                "&:hover": {
                    backgroundColor: `transparent`,
                },
                color: COLORS.METRICELL_WHITE,
            },
        },
        MuiCircularProgress: {
            circle: {
                color: COLORS.METRICELL_WHITE,
            },
        },
        MuiDialog: {
            paper: {
                backgroundColor: COLORS.METRICELL_DARK_GREY2,
            },
        },
        MuiDialogActions: {
            root: {
                padding: `8px 24px`,
            },
        },
        MuiDialogTitle: {
            root: {
                backgroundColor: COLORS.METRICELL_DARK_GREY2,
                padding: `8px 24px`,
            },
        },
        MuiListItem: {
            gutters: {
                height: `30px`,
                paddingLeft: `2px`,
                paddingRight: `2px`,
            },
        },
        MuiListItemIcon: {
            root: {
                minWidth: `26px`,
            },
        },
        MuiPaper: {
            root: {
                backgroundColor: COLORS.METRICELL_DARK_GREY,
            },
        },
        MuiStepIcon: {
            completed: {
                background: `radial-gradient(circle, #fff 0%, #fff 60%, #283845 60%)`,
            },
            text: {
                color: COLORS.METRICELL_WHITE,
                fill: COLORS.METRICELL_WHITE,
            },
        },
        MuiStepLabel: {
            label: {
                fontSize: `0.9em`,
            },
        },
        MuiStepper: {
            root: {
                fontSize: `0.9em`,
            },
        },
        MuiTable: {
            root: {
                backgroundColor: COLORS.METRICELL_LIGHT_GREY,
                color: COLORS.METRICELL_WHITE,
                borderCollapse: `separate`,
            },
        },
        MuiTableCell: {
            head: {
                borderBottom: `1px solid ${COLORS.METRICELL_DARK_GREY}`,
                borderTop: `1px solid ${COLORS.METRICELL_DARK_GREY}`,
                color: COLORS.METRICELL_WHITE,
                fontSize: 14,
                fontWeight: `bold`,
            },
            root: {
                borderBottom: `1px solid ${COLORS.METRICELL_DARK_GREY}`,
            },
        },
        MuiTableHead: {
            root: {
                backgroundColor: COLORS.METRICELL_BLUE,
                color: COLORS.METRICELL_WHITE,
            },
        },
        MuiTableRow: {
            head: {
                "&:active": {
                    backgroundColor: COLORS.METRICELL_LIGHT_GREY,
                },
                "&:focus": {
                    backgroundColor: COLORS.METRICELL_LIGHT_GREY,
                },
                "&:hover": {
                    backgroundColor: COLORS.METRICELL_LIGHT_GREY,
                },
                backgroundColor: COLORS.METRICELL_LIGHT_GREY,
                cursor: `default`,
            },
            root: {
                "&:active": {
                    backgroundColor: COLORS.METRICELL_DARK_GREY,
                },
                "&:focus": {
                    backgroundColor: COLORS.METRICELL_DARK_GREY,
                },
                "&:hover": {
                    backgroundColor: COLORS.METRICELL_DARK_GREY,
                },
                cursor: `default`,
            },
        },
        MuiToolbar: {
            root: {
                backgroundColor: COLORS.METRICELL_LIGHT_GREY,
                paddingLeft: `8px !important`,
            },
        },
        MuiTooltip: {
            tooltip: {
                fontSize: `0.8em`,
            },
        },
        MuiPopover: {
            root: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                zIndex: `10000 !important` as any,
            },
        },
        MuiSnackbarContent: {
            root: {
                lineHeight: 1.7,
            },
            message: {
                display: `flex`,
                gap: "10px",
            },
        },
        /* eslint-enable @typescript-eslint/naming-convention */
    },
    palette: {
        primary: {
            main: COLORS.METRICELL_BLUE,
        },
        secondary: {
            main: COLORS.METRICELL_DARK_GREY,
        },
        type: `dark`,
    },
    typography: {
        fontFamily: [
            `"Segoe UI"`,
            `"Verdana"`,
            `"Helvetica"`,
            `"Sans - Serif"`,
        ].join(`,`),
        fontSize: 12,
    },
});

export const ContextProvider: React.FunctionComponent = (
    props,
): React.ReactElement => (
    // We return a fragment here so that nothing is the direct child of the contexts.
    // MuiPickersUtilsProvider, for example, complains if a null child is passed this fragment ensures it is never null.
    /* eslint-disable react/jsx-no-useless-fragment */
    <MuiThemeProvider theme={APP_THEME}>
        <React.Fragment>{props.children}</React.Fragment>
    </MuiThemeProvider>
    /* eslint-enable react/jsx-no-useless-fragment */
);
